<cs-header>
  <cs-header-title>{{ getTitle() }}
    <!-- View mode: show verified icon next to title -->
    @if (isReadOnly && foodSource.icon === 'verified-user') {
      <cs-icon name="verified-user" header-title-suffix
      class="verified-icon"></cs-icon>
    }
    <!-- Shared/shareable recipe indicator -->
    @if (isRecipe() && (food.local?.shared || isImportableRecipe || isImportedRecipe)) {
      <cs-icon name="link" class="shared-icon" (click)="handleShareIconClick($event)"></cs-icon>
    }
  </cs-header-title>

  <!-- Recipe mode: tabs for Overview and Foods -->
  @if (isRecipe()) {
    <cs-header-content>
      <cs-tab [(selection)]="selectedRecipeTab" urlParam="tab">
        <cs-tab-button [value]="RecipeTab.Overview">
          <cs-icon name="ballot"></cs-icon>
          <cs-label>{{ LL.editFood.tabs.overview() }}</cs-label>
        </cs-tab-button>
        <cs-tab-button [value]="RecipeTab.Foods">
          <cs-icon name="nutrition"></cs-icon>
          <cs-label>{{ LL.editFood.tabs.ingredients() }}</cs-label>
        </cs-tab-button>
      </cs-tab>
    </cs-header-content>
  }

  <!-- View mode desktop: action buttons in header (for both Food and Recipe) -->
  @if (isReadOnly) {
    <div header-right class="header-action-buttons">
      <cs-button secondary small (click)="handleLogFoodButtonClick()">
        {{ LL.editFood.buttons.log() }}
      </cs-button>
      <cs-icon-button circular icon="liked" [fill]="food.local?.liked === 1" (click)="handleVote(+1)"></cs-icon-button>
      <cs-icon-button circular icon="disliked" [fill]="food.local?.liked === -1" (click)="handleVote(-1)"></cs-icon-button>
      <cs-icon-button icon="ellipsis-vertical" (click)="handleMoreOptionsClick($event)"></cs-icon-button>
    </div>
  }

  <!-- Create Recipe mode: dedicated ⋯ menu with just "Scan Recipe" (#3709).
       Gated on the explicit createRecipe mode (NOT isRecipe, which is true for a
       saved recipe being viewed too). -->
  @if (mode === 'createRecipe') {
    <div header-right class="header-action-buttons create-recipe-action">
      <cs-icon-button icon="ellipsis-vertical" (click)="handleCreateRecipeMenuClick($event)"></cs-icon-button>
    </div>
  }
</cs-header>

<!-- Shared recipe: import banner -->
@if (isImportableRecipe) {
  <cs-info-card type="info" [dismissible]="false" class="share-banner">
    {{ LL.editFood.buttons.sharedRecipeInfo() }}
    <button class="link" (click)="handleImportRecipe($event)">{{ LL.editFood.buttons.importRecipe() }}</button>
  </cs-info-card>
}

<!-- Imported recipe: update banners -->
@if (isImportedRecipe) {
  @if (hasOriginalUpdate) {
    <cs-info-card type="info" [dismissible]="false" [actionText]="LL.editFood.buttons.refreshFromOriginal()"
      (actionClick)="handleRefreshFromOriginal()" class="share-banner">
      {{ LL.editFood.buttons.updateAvailable() }}
    </cs-info-card>
  } @else if (originalDeleted) {
    <cs-info-card type="warning" [dismissible]="false" class="share-banner">
      {{ LL.editFood.buttons.originalUnavailable() }}
    </cs-info-card>
  } @else {
    <cs-info-card type="info" [dismissible]="false" [hideIcon]="true" class="share-banner">
      {{ LL.editFood.buttons.importedRecipe() }}
    </cs-info-card>
  }
}

<!-- Save button for edit mode - goes to header on desktop, floating on mobile -->
@if (!isReadOnly) {
  <cs-page-action-buttons>
    <cs-button primary (click)="handleAcceptButtonClicked($event, ButtonType.Header)">
      {{ LL.common.buttons.save() }}
    </cs-button>
  </cs-page-action-buttons>
}

<!-- ===== REUSABLE CARD TEMPLATES ===== -->

<!-- Macros Card (View Mode) - plain foods only. The recipe Overview has its own compact
     version below; editing this one does not change the recipe screen. -->
<ng-template #macrosCardViewTemplate>
  <cs-card class="macros-card">
    <cs-card-title>
      <span>{{ LL.editFood.labels.macros() }}</span>
      <cs-segment [(selection)]="macrosDisplayMode" (selectionChange)="onMacrosDisplayModeChange()"
        class="macros-segment" title-action>
        <cs-segment-button [value]="'serving'">{{ getServingSegmentLabel() }}</cs-segment-button>
        <cs-segment-button [value]="'100g'">{{ '100g' | untranslated }}</cs-segment-button>
      </cs-segment>
    </cs-card-title>
    <cs-card-content>
      <nutrient-list class="nutrient-macros" [nutrients]="getDisplayedNutrients()" state="food-macros" [expandable]="true">
      </nutrient-list>
    </cs-card-content>
  </cs-card>
</ng-template>

<!-- Macros Card (Recipe View Mode) - the compact five-across card the portion and meal
     screens use, in the same place they put it: at the top, above everything else.

     A recipe is NOT measured against a target the way a meal is - there is no plan target
     for "a recipe" - so the bars show how the recipe's own calories divide between its
     macros instead of how far each one is towards a target. That is what
     NutrientBarStyle.Distribution means, and it also drops the target row from the grid, so
     the card is a row shorter here than on a meal.

     Deliberately NOT the same template as the plain-food view screen above, which keeps the
     card it has (owner decision, 2026-09-09). The serving/100g dial is the same on both. -->
<ng-template #macrosCardRecipeViewTemplate>
  <cs-card class="macros-card recipe-compact">
    <cs-card-title>
      <span>{{ LL.editFood.labels.macros() }}</span>
      <cs-segment [(selection)]="macrosDisplayMode" (selectionChange)="onMacrosDisplayModeChange()"
        class="macros-segment" title-action>
        <cs-segment-button [value]="'serving'">{{ getServingSegmentLabel() }}</cs-segment-button>
        <cs-segment-button [value]="'100g'">{{ '100g' | untranslated }}</cs-segment-button>
      </cs-segment>
    </cs-card-title>
    <cs-card-content>
      <nutrient-list class="nutrient-macros" [nutrients]="getDisplayedNutrients()" state="compact-macros"
        [barStyle]="NutrientBarStyle.Distribution" [expandable]="true">
      </nutrient-list>
    </cs-card-content>
  </cs-card>
</ng-template>

<!-- Serving Sizes Card (View Mode) - shared by Food and Recipe -->
<ng-template #servingSizesCardViewTemplate let-editHandler="editHandler">
  <cs-card class="serving-sizes-card">
    <cs-card-title>
      <span>{{ LL.editFood.labels.servingSizes() }}</span>
      <cs-button tertiary icon="edit" (click)="editHandler()">
        {{ LL.common.buttons.edit() }}
      </cs-button>
    </cs-card-title>
    <cs-card-content>
      <div class="serving-sizes-list">
        @for (size of getServingSizesForDisplay(); track $index) {
          <span class="serving-size-value">{{ size }}</span>
        }
      </div>
    </cs-card-content>
  </cs-card>
</ng-template>

<!-- Details Card (Edit Mode) - shared by Food and Recipe -->
<ng-template #detailsCardEditTemplate>
  <cs-card class="details-card edit-mode">
    <cs-card-title>{{ LL.editFood.labels.details() }}</cs-card-title>
    <cs-card-content>
      <div class="input-field food-name-field">
        <cs-label>{{ LL.editFood.labels.foodName() }}</cs-label>
        <cs-input id="foodNameInput" [(value)]="food.name" [placeholder]="LL.editFood.placeholders.enterName()"
          autocomplete="off" (blur)="somethingChanged()" />
        <vld-message [validity]="validity('name')" [immediate]="checkValues"></vld-message>
      </div>
      @if (commentsEditable && food.comments) {
        <div class="input-field description-field">
          <cs-label>{{ LL.editFood.labels.description() }}</cs-label>
          <textarea class="description-textarea" [value]="food.comments"
            (input)="food.comments = $any($event.target).value"
            (blur)="somethingChanged()" rows="3"></textarea>
        </div>
      }
    </cs-card-content>
  </cs-card>
</ng-template>

<!-- ===== NON-RECIPE MODES (Food View/Edit) ===== -->
@if (!isRecipe()) {
  <cs-content vld-progress [(vldStep)]="progressStep" [(vldTotalSteps)]="totalSteps">
    <!-- ===== VIEW MODE (Foods) ===== -->
    @if (isReadOnly && !isRecipe()) {
      <div class="view-mode-content" vld-group>
        <!-- Details Card -->
        <cs-card class="details-card">
          <cs-card-title>
            <span>{{ LL.editFood.labels.details() }}</span>
            <cs-button tertiary icon="edit" (click)="setReadonly(false)">
              {{ LL.common.buttons.edit() }}
            </cs-button>
          </cs-card-title>
          <cs-card-content>
            <!-- Image -->
            @if (hasImage()) {
              <div class="food-image-container">
                <image-uploader [image]="food.image" [imageId]="food._id" [isReadOnly]="true">
                </image-uploader>
              </div>
            } @else {
              <!-- a staple's own picture (staple pictures plan, #5694): a plain image, never the
                   uploader, because there is nothing here for the reader to change. Nested rather
                   than an `@else if (… ; as …)`, which Angular allows only on a primary `@if`. -->
              @if (staplePicture(); as picture) {
                <div class="food-image-container">
                  <img class="staple-picture" [src]="picture.large" alt="" data-e2e="food-staple-picture"
                    (error)="handleStaplePictureError()">
                </div>
              }
            }
            <!-- Description -->
            @if (food.comments) {
              <div class="description">
                {{ food.comments }}
              </div>
            }
            <!-- Source -->
            @if (foodSource.shortName) {
              <div class="source-row">
                <span class="source-label">{{ LL.editFood.labels.source() }}</span>
                <a class="source-link" (click)="handleFoodSourceClick($event)">{{ foodSourceShortName }}</a>
              </div>
            }
          </cs-card-content>
        </cs-card>
        <!-- Serving Sizes and Barcode Row -->
        <div class="cards-row">
          <!-- Serving Sizes Card -->
          <ng-container
          *ngTemplateOutlet="servingSizesCardViewTemplate; context: { editHandler: handleEditServingSizesClick.bind(this) }"></ng-container>
          <!-- Barcode Card -->
          <cs-card class="barcode-card">
            <cs-card-title>
              <span>{{ LL.editFood.labels.barcode() }}</span>
              <cs-button tertiary icon="edit" (click)="handleEditBarcodeClick()">
                {{ LL.common.buttons.edit() }}
              </cs-button>
            </cs-card-title>
            <cs-card-content>
              @if (food.EANs?.length > 0) {
                <div class="barcodes-list">
                  @for (barcode of food.EANs; track barcode) {
                    <span class="barcode-value">{{ barcode }}</span>
                  }
                </div>
              } @else {
                <span class="barcode-value">{{ LL.editFood.labels.noBarcode() }}</span>
              }
            </cs-card-content>
          </cs-card>
        </div>
        <!-- Macros Card -->
        <ng-container *ngTemplateOutlet="macrosCardViewTemplate"></ng-container>
        <!-- Nutrients Card -->
        <cs-card class="nutrients-card">
          <cs-card-title>
            <span>{{ settings.nutritionLabelStyle === NutritionLabelStyle.US ? LL.editFood.labels.nutritionFacts() : LL.editFood.labels.nutritionInformation() }}</span>
            <cs-segment [(selection)]="settings.nutritionLabelStyle" (selectionChange)="setNutritionLabelOrder()"
              class="label-style-segment" title-action>
              <cs-segment-button [value]="NutritionLabelStyle.US">{{ LL.editFood.labels.usLabel() }}</cs-segment-button>
              <cs-segment-button [value]="NutritionLabelStyle.EU">{{ LL.editFood.labels.euLabel() }}</cs-segment-button>
            </cs-segment>
            <cs-button tertiary icon="edit" (click)="handleEditNutrientsClick()">
              {{ LL.common.buttons.edit() }}
            </cs-button>
          </cs-card-title>
          <cs-card-content>
            @if (labelScanConfidence != null && labelScanConfidence < 0.85) {
              <div class="label-scan-warning">
                <cs-icon name="warning"></cs-icon>
                <span>{{ LL.editFood.labelScanLowConfidence() }}</span>
              </div>
            }
            @if (labelScanJsonRepaired) {
              <div class="label-scan-warning">
                <cs-icon name="warning"></cs-icon>
                <span>{{ LL.editFood.labelScanJsonRepaired() }}</span>
              </div>
            }
            <!-- Nutrients Table -->
            <div class="nutrients-table">
              <!-- Serving size header row -->
              <div class="nutrient-row serving-size-header" [class.us-label]="settings.nutritionLabelStyle === NutritionLabelStyle.US">
                @if (settings.nutritionLabelStyle === NutritionLabelStyle.US) {
                  <span class="nutrient-name">
                    {{ LL.editFood.labels.servingSize() }}
                  </span>
                }
                @if (settings.nutritionLabelStyle !== NutritionLabelStyle.US) {
                  <span class="nutrient-name"></span>
                }
                <span class="nutrient-value">{{ getNutritionHeaderDescription() }}</span>
              </div>
              @for (entry of nutritionLabelOrderMain?.entries; track entry.tag; let i = $index) {
                <div class="nutrient-row" [class.alternate]="i % 2 === 1" [class.indent1]="entry.indent === 1"
                  [class.indent2]="entry.indent === 2">
                  <span class="nutrient-name" [class.bold]="entry.bold">{{ entry.label }}</span>
                  <!-- Special case for Energy (show both kJ and kcal in EU mode) -->
                  @if (entry.tag === 'ENER$_kJ') {
                    <span class="nutrient-value">{{ getEnergyDisplayValue() }}</span>
                  }
                  @if (entry.tag !== 'ENER$_kJ') {
                    <span class="nutrient-value">{{ getNutrientDisplayValue(entry) }}</span>
                  }
                </div>
              }
            </div>
            <!-- Show Additional Nutrients fold -->
            <div class="additional-nutrients-toggle">
              <cs-button tertiary icon-right [icon]="showAdditionalNutrients ? 'arrow-dropup' : 'arrow-dropdown'"
                (click)="toggleAdditionalNutrients()">
                {{ showAdditionalNutrients ? LL.editFood.labels.hideAdditionalNutrients() : LL.editFood.labels.showAdditionalNutrients() }}
              </cs-button>
            </div>
            <!-- Additional Nutrients (Optional) -->
            @if (showAdditionalNutrients) {
              <div class="additional-nutrients">
                @for (nutrient of getOptionalNutrients(); track nutrient.tag; let i = $index) {
                  <div class="nutrient-row" [class.alternate]="i % 2 === 1">
                    <span class="nutrient-name">{{ nutrient.name }}</span>
                    <span class="nutrient-value">{{ nutrient.valueShort }} {{ nutrient.valueUnit }}</span>
                  </div>
                }
              </div>
            }
          </cs-card-content>
        </cs-card>
      </div>
    }
    <!-- ===== EDIT MODE (Foods) ===== -->
    @if (!isReadOnly && !isRecipe()) {
      <div class="edit-mode-content" vld-group>
        <!-- Details Card -->
        <ng-container *ngTemplateOutlet="detailsCardEditTemplate"></ng-container>
        <!-- Image Card -->
        @if (showImageCard()) {
          <cs-card class="image-card">
            <cs-card-title>{{ LL.editFood.labels.image() }}</cs-card-title>
            <cs-card-content>
              <image-uploader (updateImage)="updateImageHandler($event)" [image]="food.image" [imageId]="food._id"
                [isReadOnly]="false" [offerRemoveOption]="hasImage()">
              </image-uploader>
            </cs-card-content>
          </cs-card>
        }
        <!-- Barcode Card -->
        <cs-card class="barcode-card edit-mode">
          <cs-card-title>{{ LL.editFood.labels.barcode() }}</cs-card-title>
          <cs-card-content>
            <!-- Existing Barcodes -->
            @if (food.EANs?.length > 0) {
              <div class="barcode-list">
                @for (barCode of food.EANs; track barCode) {
                  <div class="barcode-item">
                    <span class="barcode-value">{{ barCode }}</span>
                    <cs-icon name="remove-circle" class="remove-barcode" (click)="handleBarCodeClick(barCode)"></cs-icon>
                  </div>
                }
              </div>
            }
            <!-- Add Barcode Button -->
            <cs-button tertiary icon="barcode" (click)="handleAddBarcodeClick()">
              {{ LL.editFood.buttons.scanBarcode() }}
            </cs-button>
          </cs-card-content>
        </cs-card>
        <!-- Nutrients Card -->
        <cs-card class="nutrients-card edit-mode">
          <cs-card-title>
            <span>{{ LL.editFood.labels.nutrients() }}</span>
            <cs-segment [(selection)]="settings.nutritionLabelStyle" (selectionChange)="setNutritionLabelOrder()"
              class="nutrients-segment" title-action>
              <cs-segment-button [value]="NutritionLabelStyle.US">{{ LL.editFood.labels.usLabel() }}</cs-segment-button>
              <cs-segment-button [value]="NutritionLabelStyle.EU">{{ LL.editFood.labels.euLabel() }}</cs-segment-button>
            </cs-segment>
          </cs-card-title>
          <cs-card-content>
            <!-- Calories Consistency Warning -->
            @if (caloriesConsistencyWarning()) {
              <div class="nutrient-warning">
                {{ caloriesConsistencyWarning() }}
              </div>
            }
            <!-- Per-hint: show what the entered values are relative to -->
            <div class="nutrient-per-hint">
              {{ getNutritionHeaderDescription() }}
            </div>
            <!-- Nutrient Inputs -->
            <div class="nutrient-inputs">
              @for (entry of nutritionLabelOrderMain?.entries; track entry.tag) {
                <div class="nutrient-input-row" [class.indent1]="entry.indent === 1" [class.indent2]="entry.indent === 2">
                  <cs-label [class.bold]="entry.bold">{{ entry.label }}</cs-label>
                  @if (entry.displayNutrient && entry.tag !== 'ENER$_kJ') {
                    <cs-input-measure input-sequence
                      class="nutrient-measure-input" type="{{ 'nutrient_' + entry.displayNutrient.unitId }}"
                      [(canonValue)]="nutrients[entry.tag]" (canonValueChange)="nutrientInputChanged($event, entry.tag)">
                    </cs-input-measure>
                  }
                  <!-- Special case for Energy (kJ/kcal) -->
                  @if (entry.tag === 'ENER$_kJ') {
                    <div class="energy-inputs">
                      <cs-input-measure input-sequence class="nutrient-measure-input" [(canonValue)]="nutrients['ENER$_kcal']"
                        (canonValueChange)="nutrientInputChanged($event, 'ENER$_kcal')" type="nutrient_kJ">
                      </cs-input-measure>
                      <span class="energy-separator">/</span>
                      <cs-input-measure input-sequence class="nutrient-measure-input" [(canonValue)]="nutrients['ENER$_kcal']"
                        (canonValueChange)="nutrientInputChanged($event, 'ENER$_kcal')" type="nutrient_kcal">
                      </cs-input-measure>
                    </div>
                  }
                  @if (labelScanNutrientConfidences?.[entry.tag] != null && labelScanNutrientConfidences[entry.tag] < 0.85) {
                    <cs-icon name="warning" class="nutrient-low-confidence"></cs-icon>
                  }
                  <vld-message [validity]="validity('nutrient/' + entry.tag)" [immediate]="checkValues" [warning]="true"></vld-message>
                </div>
              }
            </div>
            <!-- Show Additional Nutrients fold -->
            <div class="additional-nutrients-toggle">
              <cs-button tertiary icon-right [icon]="showAdditionalNutrients ? 'arrow-dropup' : 'arrow-dropdown'"
                (click)="toggleAdditionalNutrients()">
                {{ showAdditionalNutrients ? LL.editFood.labels.hideAdditionalNutrients() : LL.editFood.labels.showAdditionalNutrients() }}
              </cs-button>
            </div>
            <!-- Additional Nutrients (Optional) -->
            @if (showAdditionalNutrients) {
              <div class="additional-nutrients edit-mode">
                @for (displayNutrient of getOptionalNutrients(); track displayNutrient.tag) {
                  <div class="nutrient-input-row">
                    <cs-label>{{ displayNutrient.name }}</cs-label>
                    <cs-input-measure input-sequence class="nutrient-measure-input" type="{{ 'nutrient_' + displayNutrient.unitId }}"
                      [(canonValue)]="nutrients[displayNutrient.tag]"
                      (canonValueChange)="nutrientInputChanged($event, displayNutrient.tag)">
                    </cs-input-measure>
                  </div>
                }
              </div>
            }
          </cs-card-content>
        </cs-card>
        <!-- Measures Card -->
        <cs-card class="serving-sizes-card edit-mode">
          <cs-card-title>{{ LL.editFood.labels.servingSizes() }}</cs-card-title>
          <cs-card-content>
            <measures-list (updateMeasures)="updateMeasuresHandler($event)"
              [measures]="food.measures" [totalWeight]="food.totalWeight_g" [isReadOnly]="false">
            </measures-list>
          </cs-card-content>
        </cs-card>
      </div>
    }
  </cs-content>
}

<!-- ===== RECIPE MODES (View/Edit with Tabs) ===== -->
@if (isRecipe()) {
  <cs-tab-content-container [animate]="animateTabContent" [selection]="activeRecipeSlot"
    [swipeEnabled]="hasClientSmallScreen()" (swipeForward)="handleSwipeForward()" (swipeBack)="handleSwipeBack()">
    <!-- Recipe View Mode - Overview Tab -->
    <cs-tab-content [value]="'view-overview'">
      <cs-content>
        <div class="recipe-overview-content" vld-group>
          <!-- Macros Card, first on the page - the same place the portion and meal screens
               put their compact card, so the macros are the first thing read on all three -->
          <ng-container *ngTemplateOutlet="macrosCardRecipeViewTemplate"></ng-container>
          <!-- Instructions Card (with optional image) -->
          @if (hasInstructions() || hasImage()) {
            <cs-card class="instructions-card">
              <cs-card-title>
                <span>{{ LL.editFood.labels.instructions() }}</span>
                <cs-button tertiary icon="edit" (click)="setReadonly(false)">
                  {{ LL.common.buttons.edit() }}
                </cs-button>
              </cs-card-title>
              <cs-card-content>
                @if (hasImage()) {
                  <div class="food-image-container">
                    <image-uploader [image]="food.image" [imageId]="food._id" [isReadOnly]="true">
                    </image-uploader>
                  </div>
                } @else {
                  @if (staplePicture(); as picture) {
                    <div class="food-image-container">
                      <img class="staple-picture" [src]="picture.large" alt="" data-e2e="food-staple-picture"
                        (error)="handleStaplePictureError()">
                    </div>
                  }
                }
                @if (hasInstructions()) {
                  <div class="instructions-editor">
                    <markdown-editor [readonly]="true" [data]="recipe?.instructions">
                    </markdown-editor>
                  </div>
                }
              </cs-card-content>
            </cs-card>
          }
          <!-- Serving Sizes Card -->
          <ng-container
          *ngTemplateOutlet="servingSizesCardViewTemplate; context: { editHandler: setReadonly.bind(this, false) }"></ng-container>
        </div>
      </cs-content>
    </cs-tab-content>
    <!-- Recipe View Mode - Ingredients Tab -->
    <cs-tab-content [value]="'view-foods'">
      <cs-content>
        <div class="recipe-foods-content" vld-group>
          <ingredient-list #ingredientsCardView [isReadOnly]="true" [ingredients]="recipe?.ingredients"
            [totalWeight]="food.totalWeight_g" [validityMessage]="validity('ingredients')"
            [validityMessageImmediate]="checkValues" [returnCallbackName]="'onIngredientEdited'" [showRecentMeals]="false"
            [emptyStateTitleKey]="'ingredientSearch.emptyState.titleRecipe'" [showHeader]="false">
          </ingredient-list>
          <cs-button tertiary icon="edit" (click)="setReadonly(false)">
            {{ LL.common.buttons.edit() }}
          </cs-button>
        </div>
      </cs-content>
    </cs-tab-content>
    <!-- Recipe Edit Mode - Overview Tab -->
    <cs-tab-content [value]="'edit-overview'">
      <cs-content>
        <div class="recipe-overview-content" vld-group>
          <!-- Empty-recipe nudge: offer the Scan Recipe feature while the recipe has no
               ingredients yet (#3709). Gated on createRecipe mode, matching the ⋯ menu. -->
          @if (mode === 'createRecipe' && !recipe?.ingredients?.length) {
            <cs-info-card type="info" [dismissible]="false"
              [message]="LL.recipeScanner.emptyRecipeHint()"
              [actionText]="LL.editFood.buttons.scanRecipe()"
              (actionClick)="openRecipeScanner()">
            </cs-info-card>
          }
          <!-- Details Card -->
          <ng-container *ngTemplateOutlet="detailsCardEditTemplate"></ng-container>
          <!-- Serving Sizes Card (Edit Mode) — recipes carry a single 'prepared' measure
               whose `amount` is the servings count (per-serving weight = total / amount).
               This was previously only rendered for non-recipe foods, so recipe edit mode
               had no way to change servings. lockToPreparedMeasure says a recipe has just
               this one measure and its name is fixed, so the list shows neither an Add
               button nor a name picker — renaming it used to delete it (#4380). -->
          <cs-card class="serving-sizes-card edit-mode">
            <cs-card-title>{{ LL.editFood.labels.servingSizes() }}</cs-card-title>
            <cs-card-content>
              <measures-list (updateMeasures)="updateMeasuresHandler($event)"
                [measures]="food.measures" [totalWeight]="food.totalWeight_g" [isReadOnly]="false"
                [lockToPreparedMeasure]="true">
              </measures-list>
            </cs-card-content>
          </cs-card>
          <!-- Image Card -->
          <cs-card class="image-card">
            <cs-card-title>{{ LL.editFood.labels.image() }}</cs-card-title>
            <cs-card-content>
              <image-uploader (updateImage)="updateImageHandler($event)" [image]="food.image" [imageId]="food._id"
                [isReadOnly]="false" [offerRemoveOption]="hasImage()">
              </image-uploader>
            </cs-card-content>
          </cs-card>
          <!-- Instructions Card -->
          <cs-card class="instructions-card">
            <cs-card-title>{{ LL.editFood.labels.instructions() }}</cs-card-title>
            <cs-card-content>
              <div class="instructions-editor">
                <markdown-editor [readonly]="false" [data]="recipe?.instructions" (updateData)="updateInstructionsHandler($event)">
                </markdown-editor>
              </div>
            </cs-card-content>
          </cs-card>
        </div>
      </cs-content>
    </cs-tab-content>
    <!-- Recipe Edit Mode - Ingredients Tab -->
    <cs-tab-content [value]="'edit-foods'">
      <cs-content>
        <div class="recipe-foods-content" vld-group>
          <ingredient-list #ingredientsCard [isReadOnly]="false" [ingredients]="recipe?.ingredients"
            [totalWeight]="food.totalWeight_g" (updateNutrients)="updateTotalNutrientsHandler($event)"
            (updateIngredients)="updateIngredientsHandler($event)" (updateTotalWeight)="updateTotalWeightHandler($event)"
            [validityMessage]="validity('ingredients')" [validityMessageImmediate]="checkValues"
            [returnCallbackName]="'onIngredientEdited'" [showRecentMeals]="false"
            [currentMealName]="food?.name"
            [emptyStateTitleKey]="'ingredientSearch.emptyState.titleRecipe'" [showHeader]="false">
          </ingredient-list>
        </div>
      </cs-content>
    </cs-tab-content>
  </cs-tab-content-container>
}